<template>
    <v-row no-gutters class="d-block">
        <v-col cols="12">
            <encabezado subtitulo="Aquí está su historial de cursos en INFOP" :cifras="cifras"/>

            <div class="barra">
                <h2 class="barra__titulo">Historial de cursos</h2>
                <v-btn text small color="primary" :loading="loading" @click="getCursos()">
                    <v-icon small left>refresh</v-icon> Actualizar
                </v-btn>
                <v-text-field
                    v-if="cursos.length > 6"
                    v-model="search"
                    class="barra__buscador"
                    prepend-inner-icon="search"
                    label="Buscar por curso, tutor o registro"
                    single-line hide-details outlined dense clearable
                    background-color="white"
                ></v-text-field>
            </div>

            <div class="controles" v-if="cursos.length">
                <v-chip-group v-model="vista" mandatory class="filtros" active-class="filtros__activo">
                    <v-chip small value="todos" outlined>Todos ({{ cursos.length }})</v-chip>
                    <v-chip small value="aprobados" outlined>Aprobados ({{ aprobados.length }})</v-chip>
                    <v-chip small value="reprobados" outlined>Reprobados ({{ reprobados.length }})</v-chip>
                </v-chip-group>

                <v-btn-toggle v-model="modo" mandatory dense class="modos" color="primary" group>
                    <v-btn v-for="m in MODOS" :key="m.valor" :value="m.valor" small text :title="m.titulo">
                        <v-icon small>{{ m.icono }}</v-icon>
                        <span class="modos__texto">{{ m.texto }}</span>
                    </v-btn>
                </v-btn-toggle>
            </div>

            <div class="tarjetas" v-if="loading && !cursos.length">
                <v-skeleton-loader v-for="n in 3" :key="n" type="article, actions"></v-skeleton-loader>
            </div>

            <div class="vacio" v-else-if="!cursos.length">
                <v-icon>history</v-icon>
                <div class="vacio__titulo">Aún no tiene cursos registrados</div>
                <div>Aparecerán aquí cuando INFOP publique los resultados de un curso a su nombre.</div>
            </div>

            <div class="vacio" v-else-if="!cursosVisibles.length">
                <v-icon>search_off</v-icon>
                <div class="vacio__titulo">Sin resultados</div>
                <div v-if="search">No hay cursos que coincidan con "{{ search }}".</div>
                <div v-else>No tiene cursos {{ vista }}.</div>
            </div>

            <!-- Tarjetas -->
            <div class="tarjetas" v-else-if="modo === 'tarjetas'">
                <tarjeta-curso v-for="item in cursosVisibles" :key="item.curso_id"
                               :curso="item" :destacada="item.curso_id == cursoBuscadoId"
                               @descargar="certificadoGenerate"/>
            </div>

            <!-- Agrupada por año -->
            <div v-else-if="modo === 'anios'">
                <section class="grupo" v-for="grupo in porAnio" :key="grupo.anio">
                    <h3 class="grupo__titulo">
                        {{ grupo.anio }}
                        <span class="grupo__detalle">{{ grupo.cursos.length }} {{ grupo.cursos.length == 1 ? 'curso' : 'cursos' }}</span>
                    </h3>
                    <div class="tarjetas">
                        <tarjeta-curso v-for="item in grupo.cursos" :key="item.curso_id"
                                       :curso="item" :destacada="item.curso_id == cursoBuscadoId"
                                       @descargar="certificadoGenerate"/>
                    </div>
                </section>
            </div>

            <!-- Lista compacta con selección para descargar varios -->
            <div v-else-if="modo === 'lista'">
                <div class="seleccion" v-if="aprobadosVisibles.length">
                    <v-checkbox
                        class="seleccion__todos"
                        :input-value="seleccion.length > 0 && seleccion.length === Math.min(aprobadosVisibles.length, MAXIMO_SELECCION)"
                        :indeterminate="seleccion.length > 0 && seleccion.length < Math.min(aprobadosVisibles.length, MAXIMO_SELECCION)"
                        :label="seleccion.length ? `${seleccion.length} seleccionado${seleccion.length == 1 ? '' : 's'}` : `Seleccionar (máximo ${MAXIMO_SELECCION})`"
                        hide-details dense
                        @change="seleccionarTodos"
                    />
                    <v-btn depressed small color="primary" :disabled="!seleccion.length" :loading="descargandoVarios" @click="descargarSeleccionados">
                        <v-icon small left>archive</v-icon>
                        Descargar seleccionados
                    </v-btn>
                    <span class="seleccion__ayuda">Marque hasta {{ MAXIMO_SELECCION }} certificados y descárguelos juntos en un archivo ZIP.</span>
                </div>

                <ul class="compacta">
                    <li class="compacta__fila" :class="{'compacta__fila--destacada': item.curso_id == cursoBuscadoId}"
                        v-for="item in cursosPorNombre" :key="item.curso_id">
                        <v-checkbox
                            v-if="item.aprobado == true"
                            v-model="seleccion"
                            :value="item.curso_id"
                            :disabled="!seleccion.includes(item.curso_id) && seleccion.length >= MAXIMO_SELECCION"
                            hide-details dense class="compacta__marca"
                        />
                        <span class="compacta__marca compacta__marca--vacia" v-else></span>
                        <div class="compacta__texto">
                            <span class="compacta__nombre">{{ item.curso }}</span>
                            <span class="compacta__meta">{{ anioDe(item) }} · Nota {{ nota(item) }}<template v-if="item.registro"> · Reg. {{ item.registro }}</template></span>
                        </div>
                        <span class="tarjeta__estado" :class="item.aprobado == true ? 'tarjeta__estado--bien' : 'tarjeta__estado--mal'">
                            {{ item.aprobado == true ? 'Aprobado' : 'Reprobado' }}
                        </span>
                        <v-btn icon small color="primary" v-if="item.aprobado == true" title="Descargar certificado" @click="certificadoGenerate(item)">
                            <v-icon>download</v-icon>
                        </v-btn>
                    </li>
                </ul>
            </div>

            <!-- Detalles: tabla con todas las columnas -->
            <v-card v-else class="detalles">
                <v-data-table
                    dense
                    :headers="encabezadosTabla"
                    :items="cursosVisibles"
                    item-key="curso_id"
                    :items-per-page="15"
                    :footer-props="$store.state.footerPropsTable"
                    :item-class="(item) => item.curso_id == cursoBuscadoId ? 'detalles__destacada' : ''"
                >
                    <template v-slot:[`item.fecha_inicio`]="{ item }">{{ item.fecha_inicio | fechaESP }}</template>
                    <template v-slot:[`item.fecha_finalizacion`]="{ item }">{{ item.fecha_finalizacion | fechaESP }}</template>
                    <template v-slot:[`item.nota`]="{ item }">{{ nota(item) }}</template>
                    <template v-slot:[`item.aprobado`]="{ item }">
                        <span class="tarjeta__estado" :class="item.aprobado == true ? 'tarjeta__estado--bien' : 'tarjeta__estado--mal'">
                            {{ item.aprobado == true ? 'Aprobado' : 'Reprobado' }}
                        </span>
                    </template>
                    <template v-slot:[`item.acciones`]="{ item }">
                        <v-btn small outlined color="primary" v-if="item.aprobado == true" @click="certificadoGenerate(item)">
                            <v-icon small left>download</v-icon> Descargar
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card>
        </v-col>

        <v-col cols="12">
            <v-dialog v-model="dialog" persistent transition="dialog-bottom-transition"
                    scrollable fullscreen>
                <v-card>
                    <v-toolbar dark color="info" dense >
                        <v-toolbar-title>Certificado Generado</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon dark @click="closePdfViewer">
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <iframe ref="objectPDF"
                            :src="url_certificado"
                            width="100%"
                            height="100%"
                            @load="iframePdfLoaded"
                            >
                    </iframe>

                </v-card>
            </v-dialog>

            <!-- Video de felicitación: se reproduce completo antes de mostrar el certificado.
                 El de la importación (MP4) suena siempre; el de la tabla "videos" (MP4 o YouTube)
                 una sola vez por curso y persona. El reproductor solo existe mientras el diálogo está abierto. -->
            <v-dialog v-model="videoDialog" persistent no-click-animation max-width="960" overlay-opacity="0.9">
                <v-card color="#000" dark class="video-felicitacion">
                    <reproductor-video
                        v-if="videoDialog && videoActual"
                        ref="videoFelicitacion"
                        :video-url="videoActual.video_url"
                        :youtube-id="videoActual.video_youtube_id"
                        autoplay
                        :controles="false"
                        @inicio="videoIniciado"
                        @fin="videoFinalizado"
                        @error="videoFallo"
                        @silencio="videoSilencio = $event"
                    />
                    <div class="video-felicitacion__pie d-flex align-center px-4 py-2">
                        <v-icon small class="mr-2" color="secondary">celebration</v-icon>
                        <span class="text-body-2">{{ descargaPendiente ? 'La descarga comenzará al terminar el video.' : 'Tu certificado se mostrará al terminar el video.' }}</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small :title="videoSilencio ? 'Activar sonido' : 'Silenciar'" @click="videoAlternarSonido">
                            <v-icon>{{ videoSilencio ? 'volume_off' : 'volume_up' }}</v-icon>
                        </v-btn>
                    </div>
                </v-card>
            </v-dialog>

            <v-dialog
                v-model="showModalCertificado"
                persistent
                width="300">
                <v-card
                    color="info"
                    dark>
                    <v-card-text class="pt-5">
                        Cargando Certificado
                        <v-progress-linear
                            indeterminate
                            color="white"
                            class="mb-0"
                        ></v-progress-linear>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-col>

    </v-row>

</template>

<script>
import Ls from "@/services/ls";
import Encabezado from "./Encabezado.vue";
import TarjetaCurso, {notaLegible} from "./TarjetaCurso.vue";
import ReproductorVideo from "@/components/Videos/ReproductorVideo.vue";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const CLAVE_MODO = "historial_modo";

export default {
    name: "MisCursosLista",
    components: {Encabezado, TarjetaCurso, ReproductorVideo},
    props: {
        // Si es requerido falso, que sea Default Vacio
        getParams: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            MODOS: [
                {valor: "tarjetas", icono: "grid_view", texto: "Tarjetas", titulo: "Vista de tarjetas"},
                {valor: "lista", icono: "checklist", texto: "Lista", titulo: "Lista compacta: elija varios certificados y descárguelos juntos"},
                {valor: "detalles", icono: "table_rows", texto: "Detalles", titulo: "Tabla con todos los datos"},
                {valor: "anios", icono: "calendar_view_month", texto: "Por año", titulo: "Agrupados por año"},
            ],
            // Mismo límite que el servidor (CertificadoController::MAXIMO_DESCARGA_VARIOS).
            MAXIMO_SELECCION: 5,
            search: "",
            // 'todos' | 'aprobados' | 'reprobados'
            vista: "todos",
            // Forma de ver el historial; se recuerda en el navegador.
            modo: Ls.get(CLAVE_MODO) || "tarjetas",
            seleccion: [],
            descargandoVarios: false,
            descargaPendiente: null,
            // Curso con el que entró por número de registro: se muestra primero y marcado.
            cursoBuscadoId: Number(Ls.get("curso_id")) || null,
            loading: false,
            cursos: [],
            dialog: false,
            showModalCertificado: false,
            url_certificado: null,
            // Video de felicitación en reproducción: {video_url, video_youtube_id, video_id, curso_id}.
            videoActual: null,
            videoDialog: false,
            videoSilencio: false,
            videoVigilante: null,
            cursoPendiente: null,
            encabezadosTabla: [
                {text: "Registro", value: "registro"},
                {text: "Curso", value: "curso"},
                {text: "Fecha inicio", value: "fecha_inicio"},
                {text: "Fecha fin", value: "fecha_finalizacion"},
                {text: "Tutor", value: "tutor"},
                {text: "Nota", value: "nota", align: "end"},
                {text: "Promoción", value: "promocion", align: "end"},
                {text: "Estado", value: "aprobado", sortable: false},
                {text: "", value: "acciones", sortable: false, align: "center"},
            ],
            filtros: {
                aprobado: this.getParams.aprobado,
            }
        }
    },

    computed: {
        aprobados() {
            return this.cursos.filter(c => c.aprobado == true);
        },
        reprobados() {
            return this.cursos.filter(c => c.aprobado != true);
        },
        cursosVisibles() {
            const base = this.vista === "aprobados" ? this.aprobados : (this.vista === "reprobados" ? this.reprobados : this.cursos);
            const texto = this.sinTildes(this.search);
            if (!texto) return base;
            return base.filter(c => this.sinTildes(`${c.curso} ${c.tutor} ${c.registro}`).includes(texto));
        },
        aprobadosVisibles() {
            return this.cursosVisibles.filter(c => c.aprobado == true);
        },
        // Lista compacta: por nombre, para ubicar un curso de un vistazo.
        cursosPorNombre() {
            return [...this.cursosVisibles].sort((a, b) => String(a.curso).localeCompare(String(b.curso), "es"));
        },
        porAnio() {
            const grupos = {};
            for (const curso of this.cursosVisibles) {
                const anio = this.anioDe(curso);
                (grupos[anio] = grupos[anio] || []).push(curso);
            }
            return Object.keys(grupos).sort((a, b) => b.localeCompare(a)).map(anio => ({anio, cursos: grupos[anio]}));
        },
        cifras() {
            if (!this.cursos.length) return [];

            const horas = this.aprobados.reduce((suma, c) => suma + (Number(c.horas_curso) || 0), 0);
            const ultima = this.cursos.map(c => String(c.fecha_finalizacion || "").slice(0, 10)).sort().pop();
            const cifras = [{icono: "school", valor: this.aprobados.length, etiqueta: "Cursos aprobados"}];

            if (this.reprobados.length) cifras.push({icono: "school", valor: this.reprobados.length, etiqueta: "Cursos reprobados"});
            if (horas) cifras.push({icono: "schedule", valor: horas.toLocaleString("es-HN"), etiqueta: "Horas certificadas"});
            if (/^\d{4}-\d{2}/.test(ultima || "")) {
                cifras.push({icono: "event_available", valor: `${MESES[Number(ultima.slice(5, 7)) - 1]} ${ultima.slice(0, 4)}`, etiqueta: "Último curso finalizado"});
            }
            return cifras;
        },
    },

    watch: {
        modo(valor) {
            Ls.set(CLAVE_MODO, valor);
            this.seleccion = [];
        },
        cursosVisibles() {
            // Lo que ya no se ve, no queda seleccionado.
            const visibles = new Set(this.aprobadosVisibles.map(c => c.curso_id));
            this.seleccion = this.seleccion.filter(id => visibles.has(id));
        },
    },

    methods: {
        sinTildes(texto) {
            return String(texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
        },
        nota(curso) {
            return notaLegible(curso);
        },
        anioDe(curso) {
            const anio = String(curso.fecha_finalizacion || curso.fecha_inicio || "").slice(0, 4);
            return /^\d{4}$/.test(anio) ? anio : "Sin fecha";
        },
        getCursos() {
            this.loading = true;
            let params = {filtros: this.filtros};
            window.axios.get('/cursos/estudiantes', {params}).then(response => {
                if (response.data.success) {

                    const cursos = response.data.data;
                    // El curso buscado por número de registro va primero; el resto, del más reciente al más antiguo.
                    const buscado = this.cursoBuscadoId;
                    cursos.sort((a, b) => (b.curso_id == buscado) - (a.curso_id == buscado) || String(b.fecha_finalizacion || "").localeCompare(String(a.fecha_finalizacion || "")));
                    this.cursos = cursos;

                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => {
                    console.log(error)
                    this.$toast.error("No se pudo listar datos.");
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        seleccionarTodos(marcar) {
            this.seleccion = marcar ? this.aprobadosVisibles.slice(0, this.MAXIMO_SELECCION).map(c => c.curso_id) : [];
        },
        descargarSeleccionados() {
            const ids = this.seleccion.slice(0, this.MAXIMO_SELECCION);
            if (!ids.length) return;

            // El video de felicitación se reproduce también antes de una descarga múltiple.
            const conVideo = this.cursos.find(c => ids.includes(c.curso_id) && this.tieneVideoPendiente(c));
            if (conVideo) {
                this.descargaPendiente = ids;
                this.cursoPendiente = null;
                this.reproducirVideoDe(conVideo);
                return;
            }

            this.descargarVarios(ids);
        },
        descargarVarios(ids) {
            this.descargandoVarios = true;
            window.axios.post('/certificados/descargar_varios', {curso_ids: ids}, {responseType: 'blob'}).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/zip'}));
                const enlace = document.createElement('a');
                enlace.href = url;
                enlace.download = `Certificados_INFOP_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
                document.body.appendChild(enlace);
                enlace.click();
                document.body.removeChild(enlace);
                window.URL.revokeObjectURL(url);
                this.seleccion = [];
            })
                .catch(async error => {
                    let mensaje = "No se pudieron descargar los certificados.";
                    try {
                        const texto = await error.response.data.text();
                        mensaje = JSON.parse(texto).message || mensaje;
                    } catch (e) { /* respuesta sin detalle */ }
                    this.$toast.error(mensaje, {timeout: 10000});
                })
                .finally(() => {
                    this.descargandoVarios = false;
                });
        },
        certificadoGenerate(curso) {
            this.cursoPendiente = curso;
            this.descargaPendiente = null;

            // Curso con video de felicitación pendiente: se reproduce completo primero.
            if (this.tieneVideoPendiente(curso)) {
                this.reproducirVideoDe(curso);
                return;
            }

            this.mostrarCertificado(curso);
        },
        /**
         * El video de la importación (sin video_id) se reproduce siempre; el de la
         * tabla "videos" solo si la persona no lo terminó ya para ese curso.
         */
        tieneVideoPendiente(curso) {
            if (!curso.video_url && !curso.video_youtube_id) return false;
            return !(curso.video_id && curso.video_visto);
        },
        reproducirVideoDe(curso) {
            this.videoActual = {
                video_url: curso.video_url || null,
                video_youtube_id: curso.video_youtube_id || null,
                video_id: curso.video_id || null,
                curso_id: curso.curso_id,
            };
            this.videoSilencio = false;
            this.videoDialog = true;

            clearTimeout(this.videoVigilante);
            // Si el video no termina en un tiempo razonable (conexión caída), se muestra el certificado igual.
            this.videoVigilante = setTimeout(() => this.videoCerrar(), 5 * 60 * 1000);
        },
        /** Registra inicio/fin en el API solo para los videos de la tabla "videos". */
        videoRegistrar(evento) {
            const v = this.videoActual;
            if (!v || !v.video_id) return;
            window.axios.post("/videos/evento", {video_id: v.video_id, evento, curso_id: v.curso_id}).catch(() => {});
        },
        /** Avisa al video predeterminado (si está en pantalla) para que se pause o reanude. */
        videoAvisar(detalle) {
            window.dispatchEvent(new CustomEvent("video-felicitacion", {detail: detalle}));
        },
        videoIniciado() {
            this.videoRegistrar("inicio");
            this.videoAvisar("inicio");
        },
        videoFinalizado() {
            const v = this.videoActual;
            if (v && v.video_id) {
                this.videoRegistrar("fin");
                // Ya visto para ese curso: la próxima descarga va directa al certificado.
                const curso = this.cursos.find(c => c.curso_id === v.curso_id);
                if (curso) curso.video_visto = true;
            }
            this.videoCerrar();
        },
        videoFallo() {
            // El video no se pudo cargar: no se bloquea el certificado.
            this.videoCerrar();
        },
        videoAlternarSonido() {
            const reproductor = this.$refs.videoFelicitacion;
            if (!reproductor) return;
            const nuevo = !reproductor.estaSilenciado();
            reproductor.silenciar(nuevo);
            this.videoSilencio = nuevo;
        },
        videoCerrar() {
            if (!this.videoDialog) return;
            clearTimeout(this.videoVigilante);
            this.videoDialog = false;
            this.videoActual = null;
            this.videoAvisar("fin");
            if (this.descargaPendiente) {
                const ids = this.descargaPendiente;
                this.descargaPendiente = null;
                this.descargarVarios(ids);
            } else if (this.cursoPendiente) {
                this.mostrarCertificado(this.cursoPendiente);
            }
        },
        mostrarCertificado(curso) {
            this.showModalCertificado = true;
            // El API devuelve un token firmado por curso; el formato antiguo
            // (base64 del id) solo se usa si el backend aún no lo envía.
            let params = curso.token_certificado || btoa(JSON.stringify({
                curso_id: curso.curso_id
            }));
            this.pedirPdf(`${this.baseURLApi}/certificados/generate/${params}`, (url) => {
                this.url_certificado = url;
                this.dialog = true;
            });
        },
        pedirPdf(url, aplicar) {
            // Se descarga por el API para poder mostrar un aviso claro si el servidor
            // está ocupado, en vez de un error crudo dentro del visor.
            window.axios.get(url, {responseType: 'blob', timeout: 180000}).then(response => {
                aplicar(URL.createObjectURL(response.data));
            })
                .catch(async error => {
                    let mensaje = "No se pudo generar el documento. Espere unos segundos e intente de nuevo.";
                    try {
                        const texto = await error.response.data.text();
                        mensaje = JSON.parse(texto).message || mensaje;
                    } catch (e) { /* respuesta sin detalle */ }
                    this.showModalCertificado = false;
                    this.dialog = false;
                    this.$toast.error(mensaje, {timeout: 12000});
                });
        },

        iframePdfLoaded() {
            this.showModalCertificado = false;
            this.dialog = true;
        },
        closePdfViewer() {
            if (this.url_certificado) URL.revokeObjectURL(this.url_certificado);
            this.url_certificado = null;
            setTimeout(() => {
                this.showModalCertificado = false;
                this.dialog = false;
            }, 500);
        },
    },
    created() {
        if (!this.MODOS.some(m => m.valor === this.modo)) this.modo = "tarjetas";
        this.getCursos();
    }
}
</script>

<style lang="scss" scoped>
@import "./tarjetas.scss";

.video-felicitacion__pie {
    background: #12172b;
}
</style>
